import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'antd';
import cs from 'classnames';

import Collapse from 'components/Collapse/Collapse';
import EyeAttachmentPreview from 'components/EyeAttachmentPreview/EyeAttachmentPreview';
import IconButton from 'components/IconButton/IconButton';
import JobseekerAvatar from 'components/JobseekerAvatar/JobseekerAvatar';
import ProfileMatch from 'components/ProfileMatch/ProfileMatch';

import jobseekersService from 'features/jobseekers/jobseekersService';

import useErrorCatch from 'hooks/useErrorCatch';

import { GTMEventLogger } from 'utils/analytics';
import { notify } from 'utils/toast/createToast';

import { JobseekerMatchesData } from 'types/jobseekers';

import {
  AlertType,
  ClientTierFeatures,
  ClientTiers,
  GTMEvents,
  JobStatus,
  JobseekerAttachmentTypes
} from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import { ReactComponent as PersonalCardIcon } from 'assets/icons/personal-card.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';

import styles from './MatchCardMini.module.scss';

interface MatchCardMiniProps {
  data: JobseekerMatchesData;
  onInviteSent: (jobseekerId: string) => void;
}

const MatchCardMini: FC<MatchCardMiniProps> = ({
  data,
  onInviteSent
}: MatchCardMiniProps) => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();

  const {
    jobseeker,
    jobseekerId,
    job,
    jobId,
    applied,
    inviteSent,
    score,
    generatedDescription
  } = data;

  const [isApplying, setIsApplying] = useState(false);

  const aiSummary = jobseeker?.aiSummary;

  const isInviteDisabled = applied || Boolean(inviteSent);
  const isActiveJob = job?.status === JobStatus.Active;
  const isInviteButtonDisabled = isApplying || !isActiveJob;
  const inviteButtonTitle = isActiveJob
    ? t('matches.button.invite')
    : t('matches.button.inviteActiveOnly');
  const attachments = jobseeker?.attachments || [];
  const jobseekerCV = attachments.find(
    (attachment) => attachment.type === JobseekerAttachmentTypes.CV
  );
  const photoUrlEndpoint = useMemo(
    () =>
      attachments.find(
        (attachment) =>
          attachment.type === JobseekerAttachmentTypes.JobseekerPhoto
      )?.url,
    [attachments]
  );

  const handleSendInvite = async () => {
    if (isInviteDisabled || isApplying) {
      return;
    }

    try {
      setIsApplying(true);
      await jobseekersService.sendJobInviteToJobseeker(jobseekerId, jobId);

      notify({
        type: AlertType.Success,
        message: t('common.operationSuccessful'),
        description: t('modal.jobseekers.invite.success', {
          name: jobseeker?.fullName || FALLBACK_VALUE,
          jobName: job?.title || FALLBACK_VALUE
        })
      });

      GTMEventLogger(GTMEvents.JobseekersListInviteSent);
      onInviteSent(jobseekerId);
    } catch (error) {
      GTMEventLogger(GTMEvents.JobseekersListInviteFailed);
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.inviteJobseeker', {
            name: jobseeker?.fullName || FALLBACK_VALUE,
            jobname: job?.title || FALLBACK_VALUE
          })
        }
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.content}>
        <div className={styles.matchBlock}>
          <div className={styles.matchHeader}>
            <div className={styles.matchTitle}>{t('common.aiAssistant')}</div>
            <ProfileMatch matchScore={score} />
          </div>
          {aiSummary && <div className={styles.aiSummary}>{aiSummary}</div>}
        </div>
        <div className={styles.jobseeker}>
          <JobseekerAvatar photoUrlEndpoint={photoUrlEndpoint} />
          <div className={styles.jobseekerName}>
            {jobseeker?.fullName || FALLBACK_VALUE}
          </div>
        </div>
        {generatedDescription ? (
          <Collapse>{generatedDescription}</Collapse>
        ) : (
          <div className={styles.descriptionFallback}>
            {t('card.jobseeker.aiSummary.placeholder')}
          </div>
        )}
      </div>

      <div className={styles.controls}>
        {jobseekerCV ? (
          <div className={styles.controlSlot}>
            <EyeAttachmentPreview
              attachments={attachments}
              attachmentType={JobseekerAttachmentTypes.CV}
              minimalTier={ClientTiers.Paid}
              feature={ClientTierFeatures.Resumes}
              renderButton={(onClick) => (
                <IconButton
                  icon={<PersonalCardIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(e);
                  }}
                  tooltip={t('card.jobseeker.button.viewResume')}
                />
              )}
            />
          </div>
        ) : null}

        <div className={styles.controlSlot}>
          <Tooltip title={inviteButtonTitle} trigger={'hover'}>
            <button
              type="button"
              className={cs(styles.inviteButton, {
                [styles.inviteButtonDisabled]: isInviteButtonDisabled
              })}
              onClick={handleSendInvite}
              disabled={isInviteButtonDisabled}
            >
              <PlusIcon />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default MatchCardMini;
